import React from 'react'
import {
    FormControl,
    FormControlLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
} from '@material-ui/core'

import {
    PipelineStepInstance,
    TransitionEnterBehavior,
} from './pipelineBuilder.types'
import { getCatalogEntry } from './stepCatalog'
import { isStepDisableable } from './stepGuards'
import { deriveEnterBehavior } from './transitionUtils'

interface CommonStepFieldsProps {
    step: PipelineStepInstance
    enabled: boolean
    enterBehavior: TransitionEnterBehavior
    readOnly?: boolean
    onEnabledChange: (value: boolean) => void
    onEnterBehaviorChange: (value: TransitionEnterBehavior) => void
}

export const CommonStepFields: React.FC<CommonStepFieldsProps> = ({
    step,
    enabled,
    enterBehavior,
    readOnly = false,
    onEnabledChange,
    onEnterBehaviorChange,
}) => {
    const catalog = getCatalogEntry(step.type)
    const canDisable = isStepDisableable(step.type)
    const showEnterBehavior = step.type !== 'prospects' && step.type !== 'leads'

    return (
        <>
            {showEnterBehavior && (
                <FormControl
                    variant="outlined"
                    size="small"
                    fullWidth
                    disabled={readOnly}
                    style={{ marginBottom: 16 }}
                >
                    <Typography variant="caption" color="textSecondary">
                        When someone arrives here
                    </Typography>
                    <Select
                        value={enterBehavior}
                        onChange={(e) =>
                            onEnterBehaviorChange(
                                e.target.value as TransitionEnterBehavior,
                            )
                        }
                    >
                        <MenuItem value="auto_start">
                            Auto-start step work immediately
                        </MenuItem>
                        <MenuItem value="queue">
                            Place in list only — PM starts work later
                        </MenuItem>
                        <MenuItem value="pm_start">
                            PM must start each person manually
                        </MenuItem>
                    </Select>
                    <Typography
                        variant="caption"
                        color="textSecondary"
                        display="block"
                        style={{ marginTop: 8 }}
                    >
                        Controls what happens when someone enters this step from
                        any upstream transition.
                    </Typography>
                </FormControl>
            )}

            {canDisable && (
                <FormControlLabel
                    control={
                        <Switch
                            color="primary"
                            checked={enabled}
                            disabled={readOnly}
                            onChange={(_, checked) => onEnabledChange(checked)}
                        />
                    }
                    label="Step enabled in pipeline"
                />
            )}

            {catalog.builderHint && step.type !== 'leads' && (
                <Typography
                    variant="caption"
                    color="textSecondary"
                    display="block"
                    style={{ marginTop: 12 }}
                >
                    {catalog.builderHint}
                </Typography>
            )}
        </>
    )
}

export function resolveStepEnterBehavior(
    step: PipelineStepInstance,
): TransitionEnterBehavior {
    return deriveEnterBehavior(step)
}
